import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from 'react';
import { guessSoraVideoIdFromPreviewEntry, isSoraModelId } from '../../utils/soraHelpers';
import { SORA_REMIX_MODEL_KEY } from '../../constants';
import type {
  StoryboardSegment,
  StoryboardSegmentPreviewEntry,
} from '../../types';
import type { StoryboardContextValue } from '../../context';
import type { FeedbackState, FramePickerState, PreviewContextMenuState } from './types';

interface UsePreviewActionsParams
  extends Pick<StoryboardContextValue, 'segments' | 'updateSegment'> {
  feedback: FeedbackState;
  findNextSegment: (segmentId: string) => StoryboardSegment | null;
}

interface UsePreviewActionsResult {
  previewContextMenu: PreviewContextMenuState | null;
  openPreviewContextMenu: (
    entry: StoryboardSegmentPreviewEntry,
    segmentId: string,
    projectId: string,
    event: MouseEvent<HTMLElement>
  ) => void;
  closePreviewContextMenu: () => void;
  handleSendRemixToNext: () => Promise<void>;
  handleRemixInCurrentSegment: () => Promise<void>;
  handleOpenFramePicker: () => void;
  framePickerState: FramePickerState | null;
  handleFramePickerClose: () => void;
  handleFrameSelected: (imageDataUrl: string) => Promise<void>;
  hasNextSegmentForMenu: boolean;
  contextMenuVideoId: string;
  isFramePickerBusy: boolean;
  canRemixInCurrentSegment: boolean;
}

export const usePreviewActions = (
  params: UsePreviewActionsParams
): UsePreviewActionsResult => {
  const { segments, updateSegment, feedback, findNextSegment } = params;
  const [previewContextMenu, setPreviewContextMenu] = useState<PreviewContextMenuState | null>(null);
  const [framePickerState, setFramePickerState] = useState<FramePickerState | null>(null);

  useEffect(() => {
    if (!previewContextMenu) return;
    const segment = segments.find((item) => item.segmentId === previewContextMenu.segmentId);
    if (!segment) {
      setPreviewContextMenu(null);
      return;
    }
    const exists = segment.previewEntries.some(
      (entry) => entry.previewId === previewContextMenu.entry.previewId
    );
    if (!exists) {
      setPreviewContextMenu(null);
    }
  }, [previewContextMenu, segments]);

  const openPreviewContextMenu = useCallback(
    (
      entry: StoryboardSegmentPreviewEntry,
      segmentId: string,
      projectId: string,
      event: MouseEvent<HTMLElement>
    ) => {
      const anchorPosition = { top: event.clientY, left: event.clientX };
      setPreviewContextMenu({
        entry,
        segmentId,
        projectId,
        anchorPosition,
      });
    },
    []
  );

  const closePreviewContextMenu = useCallback(() => {
    setPreviewContextMenu(null);
  }, []);

  const localRemixInfo = useMemo(() => {
    if (!previewContextMenu) {
      return { segment: null, videoId: '', canRemix: false } as {
        segment: StoryboardSegment | null;
        videoId: string;
        canRemix: boolean;
      };
    }
    const segment =
      segments.find((item) => item.segmentId === previewContextMenu.segmentId) ?? null;
    if (!segment) {
      return { segment: null, videoId: '', canRemix: false };
    }
    const videoId = guessSoraVideoIdFromPreviewEntry(previewContextMenu.entry) ?? '';
    if (!videoId) {
      return { segment, videoId: '', canRemix: false };
    }
    const metadata = previewContextMenu.entry.sourceMetadata;
    if (!metadata) {
      return { segment, videoId, canRemix: false };
    }
    const endpoint = metadata.endpoint?.toLowerCase().trim();
    if (!endpoint || !endpoint.includes('/v1/videos')) {
      return { segment, videoId, canRemix: false };
    }
    const modelCandidates: string[] = [];
    if (metadata.modelId) {
      modelCandidates.push(metadata.modelId);
    }
    if (metadata.modelKey) {
      modelCandidates.push(metadata.modelKey);
    }
    const hasSoraModel = modelCandidates.some((candidate) => isSoraModelId(candidate));
    if (!hasSoraModel) {
      return { segment, videoId, canRemix: false };
    }
    if (metadata.isPrivate !== false) {
      return { segment, videoId, canRemix: false };
    }
    return { segment, videoId, canRemix: true };
  }, [previewContextMenu, segments]);

  const handleSendRemixToNext = useCallback(async () => {
    if (!previewContextMenu) {
      return;
    }
    const nextSegment = findNextSegment(previewContextMenu.segmentId);
    if (!nextSegment) {
      feedback.show('未找到下一个单元，无法发送视频 ID。', 'warning');
      setPreviewContextMenu(null);
      return;
    }
    const videoId = guessSoraVideoIdFromPreviewEntry(previewContextMenu.entry);
    if (!videoId) {
      feedback.show('当前视频缺少可用于 Remix 的 ID。', 'warning');
      return;
    }
    try {
      const updates: Partial<StoryboardSegment> = { remixVideoId: videoId };
      if (nextSegment.modelId !== SORA_REMIX_MODEL_KEY) {
        updates.modelId = SORA_REMIX_MODEL_KEY;
      }
      const result = await updateSegment(nextSegment.segmentId, updates);
      if (!result) {
        throw new Error('update_segment_failed');
      }
      const title = nextSegment.title?.trim() || '下一个单元';
      feedback.show(`已将视频 ID 发送至「${title}」`, 'success');
    } catch (error) {
      console.error('[storyboard] Failed to send remix video id', error);
      feedback.show('发送视频 ID 失败，请稍后再试。', 'error');
    } finally {
      setPreviewContextMenu(null);
    }
  }, [feedback, findNextSegment, previewContextMenu, updateSegment]);

  const handleRemixInCurrentSegment = useCallback(async () => {
    if (!previewContextMenu) {
      return;
    }
    if (!localRemixInfo.segment || !localRemixInfo.canRemix || !localRemixInfo.videoId) {
      feedback.show('当前视频不满足在本单元 Remix 的条件。', 'warning');
      setPreviewContextMenu(null);
      return;
    }
    try {
      const updates: Partial<StoryboardSegment> = { remixVideoId: localRemixInfo.videoId };
      if (localRemixInfo.segment.modelId !== SORA_REMIX_MODEL_KEY) {
        updates.modelId = SORA_REMIX_MODEL_KEY;
      }
      const result = await updateSegment(localRemixInfo.segment.segmentId, updates);
      if (!result) {
        throw new Error('update_segment_failed');
      }
      const title = localRemixInfo.segment.title?.trim() || '当前单元';
      feedback.show(`已为「${title}」切换至 Sora Remix，并填入视频 ID`, 'success');
    } catch (error) {
      console.error('[storyboard] Failed to configure in-place remix', error);
      feedback.show('切换 Remix 失败，请稍后再试。', 'error');
    } finally {
      setPreviewContextMenu(null);
    }
  }, [feedback, localRemixInfo, previewContextMenu, updateSegment]);

  const handleOpenFramePicker = useCallback(() => {
    if (!previewContextMenu) {
      return;
    }
    const nextSegment = findNextSegment(previewContextMenu.segmentId);
    if (!nextSegment) {
      feedback.show('未找到下一个单元，无法选择参考帧。', 'warning');
      setPreviewContextMenu(null);
      return;
    }
    setFramePickerState({
      entry: previewContextMenu.entry,
      projectId: previewContextMenu.projectId,
      sourceSegmentId: previewContextMenu.segmentId,
      targetSegmentId: nextSegment.segmentId,
    });
    setPreviewContextMenu(null);
  }, [feedback, findNextSegment, previewContextMenu]);

  const handleFramePickerClose = useCallback(() => {
    setFramePickerState(null);
  }, []);

  const handleFrameSelected = useCallback(
    async (imageDataUrl: string) => {
      if (!framePickerState) {
        return;
      }
      const targetSegment = segments.find(
        (segment) => segment.segmentId === framePickerState.targetSegmentId
      );
      if (!targetSegment) {
        feedback.show('未找到下一个单元，无法设置参考图。', 'error');
        setFramePickerState(null);
        return;
      }
      try {
        const result = await updateSegment(targetSegment.segmentId, {
          referenceFrame: imageDataUrl,
        });
        if (!result) {
          throw new Error('update_segment_failed');
        }
        const title = targetSegment.title?.trim() || '下一个单元';
        feedback.show(`已为「${title}」设置参考图`, 'success');
      } catch (error) {
        console.error('[storyboard] Failed to apply reference frame', error);
        feedback.show('设置参考图失败，请稍后再试。', 'error');
      } finally {
        setFramePickerState(null);
      }
    },
    [feedback, framePickerState, segments, updateSegment]
  );

  const hasNextSegmentForMenu = useMemo(() => {
    if (!previewContextMenu) {
      return false;
    }
    return Boolean(findNextSegment(previewContextMenu.segmentId));
  }, [previewContextMenu, findNextSegment]);

  const contextMenuVideoId = useMemo(
    () =>
      previewContextMenu ? guessSoraVideoIdFromPreviewEntry(previewContextMenu.entry) ?? '' : '',
    [previewContextMenu]
  );

  return {
    previewContextMenu,
    openPreviewContextMenu,
    closePreviewContextMenu,
    handleSendRemixToNext,
    handleRemixInCurrentSegment,
    handleOpenFramePicker,
    framePickerState,
    handleFramePickerClose,
    handleFrameSelected,
    hasNextSegmentForMenu,
    contextMenuVideoId,
    isFramePickerBusy: Boolean(framePickerState),
    canRemixInCurrentSegment: localRemixInfo.canRemix,
  };
};
